/**
 * Selectors over the DV360/TTD final plan (launcher's parsed_response.json).
 *
 * Canonical (DV360) shape these selectors expect:
 *   plan.platform: 'dv360' | 'ttd'
 *   plan.campaign.{displayName, campaignFlight.plannedDates.{startDate,endDate}}
 *   plan.insertionOrders[].{displayName, pacing.dailyMaxImpressions,
 *                           budget.budgetSegments[].budgetAmountInImpressions,
 *                           lineItems[string-ids]}
 *   plan.lineItems[].{lineItemId, placementId, displayName, tactic,
 *                     flight.dateRange.{startDate,endDate}, pacing, ...}
 *
 * TTD plans (parsed_response.json with `Platform: "TTD"`) use a different
 * shape: `Campaigns[]`/`AdGroups[]` (PascalCase except `adGroupId/Name`),
 * impression budgets at the campaign level, ad groups inherit flight from
 * their parent. `normalizeForDashboard` below maps that to the canonical
 * shape so the dashboard renders without per-platform branching. Each
 * normalized object carries `__refPath` — the ORIGINAL TTD JSON Pointer —
 * so the @-reference-into-Copilot button emits the path the agent's
 * `apply_plan_edit` actually speaks.
 *
 * No dollar budgets live on this plan — DV360/TTD are both impression-based
 * for plan-review purposes. All aggregates and displays are impressions.
 */

// -----------------------------------------------------------------------------
// Iteration helpers
// -----------------------------------------------------------------------------

export function iterateLineItems(plan) {
  if (!plan) return [];
  const idIndex = new Map();
  for (const li of plan.lineItems || []) {
    if (li && li.lineItemId) idIndex.set(li.lineItemId, li);
  }
  const out = [];
  for (const io of plan.insertionOrders || []) {
    const refs = Array.isArray(io.lineItems) ? io.lineItems : [];
    for (const ref of refs) {
      const li = typeof ref === 'string' ? idIndex.get(ref) : ref;
      if (li) out.push({ io, li });
    }
  }
  return out;
}

// -----------------------------------------------------------------------------
// Aggregates (all impression-based)
// -----------------------------------------------------------------------------

export function sumImpressions(plan) {
  let total = 0;
  for (const io of plan?.insertionOrders || []) {
    for (const seg of io.budget?.budgetSegments || []) {
      total += Number(seg.budgetAmountInImpressions || 0);
    }
  }
  return total;
}

export function countLineItems(plan) {
  return iterateLineItems(plan).length;
}

/**
 * Group impressions by a key extracted per-line-item. The IO's budgeted
 * impressions are split evenly across its line items for a per-LI
 * contribution.
 */
export function groupImpressions(plan, keyFn) {
  const out = {};
  for (const io of plan?.insertionOrders || []) {
    const refs = Array.isArray(io.lineItems) ? io.lineItems : [];
    const liCount = refs.length || 1;
    const ioTotal = (io.budget?.budgetSegments || [])
      .reduce((acc, s) => acc + Number(s.budgetAmountInImpressions || 0), 0);
    const perLi = ioTotal / liCount;
    const idIndex = new Map();
    for (const li of plan.lineItems || []) {
      if (li && li.lineItemId) idIndex.set(li.lineItemId, li);
    }
    for (const ref of refs) {
      const li = typeof ref === 'string' ? idIndex.get(ref) : ref;
      if (!li) continue;
      const key = keyFn(li, io) || 'other';
      out[key] = (out[key] || 0) + perLi;
    }
  }
  return out;
}

// -----------------------------------------------------------------------------
// Flight helpers
// -----------------------------------------------------------------------------

export function campaignFlightStr(plan) {
  const p = plan?.campaign?.campaignFlight?.plannedDates;
  if (p?.startDate && p?.endDate) {
    return `${formatDvDate(p.startDate)} — ${formatDvDate(p.endDate)}`;
  }
  return '—';
}

export function flightDaysInclusive(plan) {
  const s = formatDvDate(plan?.campaign?.campaignFlight?.plannedDates?.startDate);
  const e = formatDvDate(plan?.campaign?.campaignFlight?.plannedDates?.endDate);
  if (!s || !e) return 0;
  const a = new Date(s);
  const b = new Date(e);
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.max(1, Math.round((b - a) / (24 * 3600 * 1000)) + 1);
}

export function formatDvDate(d) {
  if (!d) return null;
  if (typeof d === 'string') return d;
  if (d.year && d.month && d.day) {
    return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
  }
  return null;
}

// -----------------------------------------------------------------------------
// TTD → canonical (DV360-shaped) adapter for the dashboard.
// -----------------------------------------------------------------------------

function isoToYmd(iso) {
  if (!iso) return null;
  const m = String(iso).match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}

/**
 * Translate a TTD parsed_response.json into the DV360-shaped object the
 * dashboard renders. Returns the input unchanged for DV360 plans (or
 * anything we don't recognise as TTD).
 *
 * Each normalized insertionOrder / lineItem carries a `__refPath` field
 * holding the ORIGINAL TTD JSON Pointer (e.g. `/Campaigns/0`,
 * `/AdGroups/3`). The @-reference button reads that to emit a pointer the
 * agent's apply_plan_edit can act on.
 */
export function normalizeForDashboard(plan) {
  if (!plan) return plan;
  const platform = String(plan.platform || plan.Platform || '').toLowerCase();
  if (platform !== 'ttd') return plan;

  const campaigns = Array.isArray(plan.Campaigns) ? plan.Campaigns : [];
  const adGroups = Array.isArray(plan.AdGroups) ? plan.AdGroups : [];

  const insertionOrders = campaigns.map((c, idx) => ({
    __refPath: `/Campaigns/${idx}`,
    displayName: c.CampaignName,
    pacing: c.DailyBudgetInImpressions != null
      ? { dailyMaxImpressions: c.DailyBudgetInImpressions }
      : undefined,
    budget: {
      budgetSegments: [{
        budgetAmountInImpressions: c.BudgetInImpressions || 0,
        dateRange: {
          startDate: isoToYmd(c.StartDate),
          endDate: isoToYmd(c.EndDate),
        },
      }],
    },
    // Campaigns hold AdGroup references as a string array — same shape
    // DV360 IOs use for their lineItems refs, so this drops in unchanged.
    lineItems: Array.isArray(c.AdGroups) ? c.AdGroups : [],
  }));

  // Index every ad group → its parent Campaign so we can derive a per-AG
  // daily cap. TTD's AdGroups carry frequency configs and bid CPMs but no
  // stored impression cap of their own — the only impression numbers live
  // on the Campaign (`DailyBudgetInImpressions`). We surface the
  // even-split estimate (campaign cap ÷ sibling count) on each AG row
  // marked with __estimated so the dashboard can prefix it with "≈" and
  // signal that this isn't a directly-edited field.
  const adGroupParent = new Map();
  for (const c of campaigns) {
    const refs = Array.isArray(c.AdGroups) ? c.AdGroups : [];
    for (const ref of refs) {
      if (typeof ref === 'string') adGroupParent.set(ref, c);
    }
  }

  const lineItems = adGroups.map((ag, idx) => {
    const parent = adGroupParent.get(ag.adGroupId);
    const parentDailyCap = parent?.DailyBudgetInImpressions;
    const siblingCount = Array.isArray(parent?.AdGroups) ? parent.AdGroups.length : 0;
    const perAgEstimate = (parentDailyCap != null && siblingCount > 0)
      ? Math.round(Number(parentDailyCap) / siblingCount)
      : null;
    return {
      __refPath: `/AdGroups/${idx}`,
      lineItemId: ag.adGroupId,
      displayName: ag.adGroupName,
      tactic: ag.tactic,
      // TTD ad groups inherit flight from their parent Campaign. The
      // dashboard's resolveLineItemFlight already falls back to the parent
      // IO's first budget segment, which after the mapping above is the
      // parent Campaign's StartDate/EndDate.
      flight: undefined,
      pacing: perAgEstimate != null
        ? { dailyMaxImpressions: perAgEstimate, __estimated: true }
        : undefined,
    };
  });

  // Synthesize a campaign-level summary the dashboard's CampaignSummary
  // expects. Title = Initiative when available; flight = min(StartDate)
  // → max(EndDate) across Campaigns.
  let minStart = null;
  let maxEnd = null;
  for (const c of campaigns) {
    const s = isoToYmd(c.StartDate);
    const e = isoToYmd(c.EndDate);
    if (s && (!minStart || s < minStart)) minStart = s;
    if (e && (!maxEnd || e > maxEnd)) maxEnd = e;
  }

  return {
    ...plan,
    platform: 'ttd',
    advertiserName: plan.AdvertiserName || plan.advertiserName,
    campaign: {
      displayName: plan.Initiative
        || [plan.AdvertiserName, plan.FiscalYear, plan.TimePeriod].filter(Boolean).join(' · ')
        || '—',
      advertiserName: plan.AdvertiserName,
      campaignFlight: {
        plannedDates: (minStart || maxEnd)
          ? { startDate: minStart, endDate: maxEnd }
          : undefined,
      },
    },
    insertionOrders,
    lineItems,
    // Platform-native terminology the dashboard surfaces in column headers,
    // metric labels, and short summaries. The DV360 path uses the same map
    // via the planTerms() helper below — TTD just overrides the four words
    // that differ. Singular + plural to keep grammar correct in chips like
    // "3 LIs" / "2 AGs".
    __terms: {
      io: 'Campaign',
      ios: 'Campaigns',
      li: 'Ad Group',
      lis: 'Ad Groups',
      liShort: 'AG',
      ioDailyCapLabel: 'Campaign daily cap',
    },
  };
}

/**
 * Resolve the dashboard terminology for a plan. Returns the TTD-native
 * "Campaign / Ad Group" labels when the normalizer attached __terms,
 * otherwise the DV360-native "Insertion Order / Line Item" defaults.
 */
export function planTerms(plan) {
  return plan?.__terms || {
    io: 'Insertion Order',
    ios: 'Insertion Orders',
    li: 'Line Item',
    lis: 'Line Items',
    liShort: 'LI',
    ioDailyCapLabel: 'IO daily cap',
  };
}
